import {
	getFrequencyOptions,
	getFullOrderOptions,
	getOrderOptions,
	type FullOrderViewModel,
	getLabDrawOptions,
	getGoalsAndInterventionsByOrderOptions,
	OrderStatusEnum,
	getInDraftOrderForUserOptions,
	getLabDrawsByOrderOptions,
	getWoundOrdersForOrderOptions,
	getEvaluationsAndRoutineFrequenciesForOrderOptions
} from '@generated';
import { useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

export function useGetFullOrderOptions(orderId: string) {
	const queryClient = useQueryClient();
	return getFullOrderOptions(orderId, (result: FullOrderViewModel) => {
		queryClient.setQueryData(getOrderOptions(result.id).queryKey, result);
		for (const frequency of result.evaluations) {
			queryClient.setQueryData(getFrequencyOptions(frequency.id).queryKey, frequency);
		}
		for (const frequency of result.routine) {
			queryClient.setQueryData(getFrequencyOptions(frequency.id).queryKey, frequency);
		}
		queryClient.setQueryData(getEvaluationsAndRoutineFrequenciesForOrderOptions(result.id).queryKey, {
			evaluations: result.evaluations,
			routine: result.routine,
			routinePrnFrequencies: result.routinePrnFrequencies,
			suggestedFrequenciesApplied: result.suggestedFrequenciesApplied,
			discontinuedFrequencies: result.discontinuedFrequencies,
			discontinuedRoutinePrnFrequencies: result.discontinuedRoutinePrnFrequencies
		});
		for (const frequency of result.labs) {
			queryClient.setQueryData(getLabDrawOptions(frequency.id).queryKey, frequency);
		}
		queryClient.setQueryData(getLabDrawsByOrderOptions(orderId).queryKey, result.labs);

		queryClient.setQueryData(getGoalsAndInterventionsByOrderOptions(orderId).queryKey, {
			goals: result.goals,
			interventions: result.interventions
		});

		queryClient.setQueryData(getWoundOrdersForOrderOptions(result.id).queryKey, result.woundOrders);

		if (result.orderStatusId === OrderStatusEnum.Drafted) {
			queryClient.setQueryData(getInDraftOrderForUserOptions(result.stayId, result.createdById).queryKey, result);
		}
	});
}

export function useGetEvaluationOrderOptions(orderId: string) {
	return useGetFullOrderOptions(orderId);
}

export function useOrderItems<TItem, TOptions extends object>(
	orderId: string,
	getItemsOptions: (orderId: string, onResult: (result: TItem) => void) => TOptions,
	update: (fullOrder: FullOrderViewModel, result: TItem) => void
) {
	const queryClient = useQueryClient();
	const fullOrderQueryKey = getFullOrderOptions(orderId).queryKey;

	return {
		...getItemsOptions(orderId, (result: TItem) => {
			const fullOrder = queryClient.getQueryData<FullOrderViewModel>(fullOrderQueryKey);
			if (fullOrder) {
				queryClient.setQueryData(
					fullOrderQueryKey,
					produce(fullOrder, draft => {
						update(draft, result);
					})
				);
			}
		}),
		initialData: () => {
			const fullOrder = queryClient.getQueryData<FullOrderViewModel>(fullOrderQueryKey);
			if (!fullOrder) {
				return undefined;
			}
			return fullOrder;
		},
		initialDataUpdatedAt: () => queryClient.getQueryState(fullOrderQueryKey)?.dataUpdatedAt
	};
}
